import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAllEsmeByCommitId } from '../../../store/esme/esmeActions';
import { fetchAllSmscByCommitId } from '../../../store/smsc/smscActions';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EsmeTable from '../components/esme/EsmeTable';
import { selectAllEsmeByCommitId, selectErrorsForEsme, selectIsLoadingEsme } from '../../../store/esme/esmeSelector';
import { selectAllSmscByCommitId, selectErrorForSmsc, selectIsLoadingSmsc } from '../../../store/smsc/smscSelector';
import SmscTable from '../components/smsc/SmscTable';
import Grid from '@mui/material/Grid';
import SchemaSelectContainer from './SchemaSelectContainer';
import { VersionableId } from '../../../types/versionableId';
import { FormMode } from '../../../types/formMode';
import Alert from '@mui/material/Alert';
import TabPanel from '../../../shared/components/TabPanel';
import { useSelectedSchema } from '../../../hooks/useSelectedSchema';
import { AppErrorList, useAppErrorList } from '../../../shared/components/AppErrorList';
import { selectErrorsForSchemas } from '../../../store/schemas/schemasSelector';
import Button from '@mui/material/Button';
import CompareIcon from '@mui/icons-material/Compare';
import HistoryIcon from '@mui/icons-material/History';
import { Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CONFIGS_COMPARE_PATH, CONFIGS_HISTORY_PATH } from '../../../routes';

type Props = {
  readonly showEsmeForm: (mode: FormMode, esmeId?: VersionableId) => void;
  readonly showEsmeDeleteDialog: (esmeId: VersionableId) => void;
  readonly showSmscForm: (mode: FormMode, smscId?: VersionableId) => void;
  readonly showSmscDeleteDialog: (smscId: VersionableId) => void;
};

const ConfigurationContainer = (props: Props) => {
  const {
    showEsmeForm,
    showEsmeDeleteDialog,
    showSmscForm,
    showSmscDeleteDialog,
  } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [tab, setTab] = useState<number>(0);
  const [selectedSchema] = useSelectedSchema();
  const [{ errors, hasErrors }, { setErrors }] = useAppErrorList();

  const commitId = selectedSchema?.headCommitId;

  const esmeList = useAppSelector(state => selectAllEsmeByCommitId(state, commitId));
  const isLoadingEsme = useAppSelector(selectIsLoadingEsme);

  const smscList = useAppSelector(state => selectAllSmscByCommitId(state, commitId));
  const isLoadingSmsc = useAppSelector(selectIsLoadingSmsc);

  const schemaErrors = useAppSelector(selectErrorsForSchemas);
  const esmeErrors = useAppSelector(selectErrorsForEsme);
  const smscErrors = useAppSelector(selectErrorForSmsc);

  useEffect(() => {
    if (commitId) {
      dispatch(fetchAllEsmeByCommitId(commitId));
      dispatch(fetchAllSmscByCommitId(commitId));
    }
  }, [dispatch, commitId]);

  useEffect(() => {
    setErrors([
      ...schemaErrors,
      ...esmeErrors,
      ...smscErrors,
    ]);
  }, [setErrors, schemaErrors, esmeErrors, smscErrors]);

  return (
    <Grid container spacing={2}>
      { hasErrors && (
        <Grid item md={12}>
          <AppErrorList errors={errors} />
        </Grid>
      )}

      <Grid item md={3} xs={12}>
        <SchemaSelectContainer />
      </Grid>

      { selectedSchema && (
        <Grid item md={9} xs={12} alignSelf='self-end'>
          <Stack direction='row' spacing={2}>
            <Button
              size='small'
              variant='text'
              startIcon={<HistoryIcon />}
              onClick={() => navigate(CONFIGS_HISTORY_PATH)}
            >
              Show history
            </Button>

            <Button
              size='small'
              variant='text'
              startIcon={<CompareIcon />}
              onClick={() => navigate(CONFIGS_COMPARE_PATH)}
            >
              Compare changes
            </Button>
          </Stack>
        </Grid>
      )}

      <Grid item xs={12}>
        <Paper style={{ backgroundColor: '#F5F5F5' }}>
          <Tabs
            style={{ minHeight: '100%' }}
            value={tab}
            onChange={(event, value) => setTab(value)}
            indicatorColor='primary'
            textColor='primary'
            variant='scrollable'
          >
            <Tab label='ESME' />
            <Tab label='SMSC' />
          </Tabs>
        </Paper>
        { selectedSchema ? (
          <>
            <TabPanel value={tab} index={0}>
              <EsmeTable
                esmeList={esmeList}
                isLoading={isLoadingEsme}
                onAddClick={() => showEsmeForm(FormMode.ADD)}
                onViewClick={esmeId => showEsmeForm(FormMode.VIEW, esmeId)}
                onEditClick={esmeId => showEsmeForm(FormMode.EDIT, esmeId)}
                onDeleteClick={esmeId => showEsmeDeleteDialog(esmeId)}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <SmscTable
                smscList={smscList}
                isLoading={isLoadingSmsc}
                onAddClick={() => showSmscForm(FormMode.ADD)}
                onViewClick={smscId => showSmscForm(FormMode.VIEW, smscId)}
                onEditClick={smscId => showSmscForm(FormMode.EDIT, smscId)}
                onDeleteClick={smscId => showSmscDeleteDialog(smscId)}
              />
            </TabPanel>
          </>
        ) : (
          <Alert severity='info'>Select a schema to view configuration</Alert>
        )}
      </Grid>
    </Grid>
  );
};

export default ConfigurationContainer;
